import { useCallback, useEffect, useState, type ReactNode } from "react";

import { services, type Transport } from "@tdelivr/api-client";
import type {
  PlatformIndexCheckpointSummary,
  PlatformIndexDeadLetterSummary,
  PlatformIndexRebuildSummary,
} from "@tdelivr/api-client/generated/search/search-indexer.js";

import { boundedNote, PER_FETCH } from "../board.ts";
import {
  SEARCH_INDEX_SCOPE,
  indexRows,
  listingNote,
  statesPresent,
  type IndexListings,
  REBUILD_REFUSAL,
} from "../search-index.ts";
import type { Session } from "../session.ts";
import { stateForError, type ViewState } from "../view-state.ts";
import { RecordsBoard } from "./RecordsBoard.tsx";

/**
 * `search` in `16 WP15 Platform Machinery.dc.html`: the search index control board.
 *
 * ```
 * search:['Search index control','SEA-02',
 *   'Projection checkpoints, dead-lettered events and rebuilds for every search index.',
 *   [['Indexes','12','done','healthy'],['Lag','42 s','caution','orders index'],
 *    ['Rebuilding','1','active','drivers'],['Dead-lettered','2','failed','']],
 *   ['Index','Checkpoint','Lag'],
 *   [['orders','offset 1,882,104','42 s','caution','lagging'],
 *    ['drivers','rebuild 61%','—','active','rebuilding'],
 *    ['merchants','offset 88,210','1 s','done','current']],
 *   'info','A rebuild never removes the live index until the new one is caught up.']
 * ```
 *
 * What is drawn from data and what is not:
 *
 * | drawn                                  | source                                                      |
 * | -------------------------------------- | ----------------------------------------------------------- |
 * | the rows, one per index alias          | `SEA-02 GET /v1/platform/index-checkpoints` (ADR 0064)       |
 * | `Index` — `orders`                     | `checkpoints.indexAlias`, rolled up from the table's grain   |
 * | `Checkpoint` — `offset 1,882,104`      | `checkpoints.lastSequence`, in the design's own grouping     |
 * | `Checkpoint` — `rebuild 61%`           | the word `rebuilding`; **no figure is published.** Q-221     |
 * | `Lag` — `42 s`                         | `now − checkpoints.indexedThrough`. ADR 0064 §1              |
 * | `caution · lagging`, `done · current`  | the lag, against a threshold **nothing publishes**           |
 * | `active · rebuilding`                  | `GET /v1/platform/index-rebuilds`, status `requested`        |
 * | the dead-letter count on every panel   | `GET /v1/platform/index-dead-letters`                        |
 * | the four KPI tiles                     | **nothing** — R-04 and Q-178; `healthy` is ADR 0064 §8 too   |
 * | `Rebuild index`                        | drawn and refused — `REBUILD_REFUSAL`, ADR 0064 §7           |
 *
 * ## Three listings on one page, one scope, and only one of them is the grid
 *
 * ADR 0064 §2 published three operations because the board draws three row shapes with three
 * primary keys, and **one** scope because all three grant the same thing. So one scope check and
 * three calls — and the three are not equal and are not treated as equal:
 *
 *   - **The checkpoints are the grid.** A failure there is the board's failure state; there is
 *     nothing to draw without them.
 *   - **The rebuilds are one badge and one panel line.** A failure there means no row is shown as
 *     `rebuilding` and an alias reached only by a rebuild is missing from the board.
 *   - **The dead letters are one badge and one panel line.** A failure there means no row is shown
 *     as `dead-lettered`.
 *
 * Both secondaries degrade to the design's own `—` and keep every index on screen, with a sentence
 * under the grid saying which listing did not answer. The alternative — one `Promise.all` and one
 * error state — would hide every search index on the platform because a second listing of the same
 * service was unreachable, on the one page an operator opens *because* indexing is failing. That is
 * `ui/EventCatalogue.tsx`'s shape, and it is the shape here three times instead of twice.
 *
 * ## What a platform-scoped read did not give this page
 *
 * One of the four things ADR 0064 lists as undecided reaches a cell: the rebuild percentage
 * (Q-221), which the `Checkpoint` cell answers with the word and no figure. Two more are the tile
 * row, which no board of this console draws. The fourth is the row grain (Q-220), which this board
 * resolves by rolling up as ADR 0064 §8 says it should and saying so in the footnote and on every
 * row's `title`.
 *
 * One thing is this board's own finding rather than one it inherited: **the `State` badge needs a
 * threshold nothing publishes.** `lagging` and `current` are the design's words for two sides of a
 * line, the duration is measured, and no freshness objective exists in SEA-02 or anywhere else for
 * an index alias. `../search-index.ts`'s `CURRENT_WITHIN_MS` reuses this console's own
 * `STALE_AFTER_MS` rather than inventing a second number, and says so on every badge.
 */

const searchIndexer = services.search__search_indexer;

export interface SearchIndexProps {
  readonly transport: Transport;
  readonly session: Session;
  /** Innermost last: `Platform › Machinery › Search index control`, the design's trail. */
  readonly crumbs: readonly string[];
}

/** The design's `PAGES` row, so the words on screen and the words here cannot drift apart. */
const TITLE = "Search index control";
const SOURCE = "SEA-02";
const DESCRIPTION =
  "Projection checkpoints, dead-lettered events and rebuilds for every search index.";
const COLUMNS = ["Index", "Checkpoint", "Lag"] as const;
const NOTE = "A rebuild never removes the live index until the new one is caught up.";
/** The design's own `noteK` for this page is `info`, which its `NK` map draws in the sky tint. */
const NOTE_KIND = "info" as const;
/** The design's `FORMS.search[0]`, which is the word on its heading button. */
const PRIMARY_LABEL = "Rebuild index";

export function SearchIndex({ transport, session, crumbs }: SearchIndexProps): ReactNode {
  const [checkpoints, setCheckpoints] = useState<
    readonly PlatformIndexCheckpointSummary[] | undefined
  >(undefined);
  const [rebuilds, setRebuilds] = useState<readonly PlatformIndexRebuildSummary[] | undefined>(
    undefined,
  );
  const [deadLetters, setDeadLetters] = useState<
    readonly PlatformIndexDeadLetterSummary[] | undefined
  >(undefined);
  const [truncated, setTruncated] = useState(false);
  const [failure, setFailure] = useState<ViewState | undefined>(undefined);
  const [state, setState] = useState("");
  /**
   * The instant the lag is measured against, pinned at the moment the three listings answered.
   *
   * Pinned rather than read per render, for the reason ADR 0064 §1 publishes an instant rather than
   * a duration in the first place: a `Date.now()` inside a cell makes the same row re-render to a
   * different number for no reason the operator caused, and makes a fidelity capture unrepeatable.
   * Pressing retry re-reads it, which is the control that means "tell me where it is now".
   */
  const [measuredAt, setMeasuredAt] = useState(() => Date.now());

  const load = useCallback(async () => {
    setCheckpoints(undefined);
    setRebuilds(undefined);
    setDeadLetters(undefined);
    setFailure(undefined);

    /*
     * One scope for three listings, asked for before any of them is called.
     *
     * ADR 0064 §2: three operations, one scope, because a scope is what it grants and these grant
     * the same thing. A 403 from any of the three renders the same state, so this is not the only
     * guard; it is the one that makes the literal appear in this application's source, which is
     * what `channel-scopes.mjs` derives the Keycloak grant from. ADR 0049 §3.
     */
    if (!session.scopes.has(SEARCH_INDEX_SCOPE)) {
      setFailure({ kind: "forbidden", missingScopes: [SEARCH_INDEX_SCOPE] });
      return;
    }

    /*
     * The three platform listings, not the three internal operations.
     *
     * SEA-02's others are `projectSearchEvent`, `getProjectionCheckpoint` and `rebuildTenantIndex`,
     * all `/internal/v1/tenants/{tenantId}/indexes/{indexAlias}/…` and all tenant-bounded, so there
     * is no wrong one to call by accident here — the by-identifier checkpoint read needs an alias
     * and a source this board exists to discover.
     *
     * Started together and awaited apart, which is this file's header: the checkpoints are the grid
     * and the other two are a badge and a panel line each.
     */
    const client = searchIndexer.createSearchIndexerClient(transport);
    const checkpointPage = client.listPlatformIndexCheckpoints(PER_FETCH);
    const rebuildPage = client.listPlatformIndexRebuilds(PER_FETCH);
    const deadLetterPage = client.listPlatformIndexDeadLetters(PER_FETCH);

    /*
     * Both secondaries' rejections are caught here and not left for the `await`s below, because an
     * unhandled rejection on a promise started before an earlier `await` threw is an unhandled
     * rejection warning rather than a handled failure.
     */
    const rebuilt = rebuildPage.then(
      (page) => page,
      () => undefined,
    );
    const dead = deadLetterPage.then(
      (page) => page,
      () => undefined,
    );

    setMeasuredAt(Date.now());

    try {
      const page = await checkpointPage;
      setCheckpoints(page.checkpoints);
      setTruncated(page.nextAfter !== null);
    } catch (error) {
      setFailure(stateForError(error, [SEARCH_INDEX_SCOPE]));
      return;
    }

    setRebuilds((await rebuilt)?.rebuilds);
    setDeadLetters((await dead)?.deadLetters);
  }, [transport, session.scopes]);

  useEffect(() => {
    void load();
  }, [load]);

  const listings: IndexListings = { checkpoints, rebuilds, deadLetters };
  const rows = checkpoints === undefined ? undefined : indexRows(listings, measuredAt);
  const shown = rows?.filter((row) => state === "" || row.state.label === state);

  const bounded = boundedNote(
    checkpoints?.length,
    truncated,
    "projection checkpoints",
    "ordered by tenant, then index, then source",
  );
  const footnote = rows === undefined ? undefined : listingNote(listings, rows);

  return (
    <RecordsBoard
      crumbs={crumbs}
      title={TITLE}
      source={SOURCE}
      description={DESCRIPTION}
      columns={COLUMNS}
      rows={shown}
      {...(failure === undefined ? {} : { failure })}
      onRetry={() => {
        void load();
      }}
      purpose="No search index has a projection checkpoint, a rebuild request or a dead-lettered event on any tenant. A row appears here once SEA-02 projects its first event into a configured index."
      primary={{ label: PRIMARY_LABEL, refusal: REBUILD_REFUSAL }}
      filter={{
        // The design's own word for unfiltered, from its `gridUI`: `All states`.
        unfiltered: "All states",
        options: (rows === undefined ? [] : statesPresent(rows)).map((label) => ({
          value: label,
          label,
        })),
        value: state,
        onChange: setState,
      }}
      note={{ kind: NOTE_KIND, glyph: "info", text: NOTE }}
      {...(bounded === undefined ? {} : { bounded })}
      {...(footnote === undefined ? {} : { footnote })}
      exportName="search-index-control"
      detailSubtitle={`${TITLE} · ${SOURCE}`}
      detailNote={NOTE}
    />
  );
}
